import { inject, injectable, injectWithTransform } from "tsyringe";
import { Geo, Logger, LoggerTransformer, waitFor } from "@identity/utils";
import { ConsentProviderInterface } from "../consent-provider-interface";
import { ConsentResolvedCallback } from "../../types";
import { WINDOW } from "@identity/symbols";
import { KetchTracker } from "./ketch-tracker";

export enum KetchCommands {
  on = "on",
  once = "once",
  off = "off",
}

export enum KetchEvents {
  consent = "consent",
  userConsentUpdated = "userConsentUpdated",
  willShowExperience = "willShowExperience",
  hideExperience = "hideExperience",
  regionInfo = "regionInfo",
  geoip = "geoip",
  jurisdiction = "jurisdiction",
  environment = "environment",
  identities = "identities",
}

export interface KetchSetup {
  purposes: {
    actively_scan_device_characteristics_for_identification: boolean;
    create_profiles_for_personalised_advertising: boolean;
    create_profiles_to_personalise_content: boolean;
    deliver_and_present_advertising_and_content: boolean;
    develop_and_improve_services: boolean;
    ensure_security__prevent_and_detect_fraud__and_fix_errors: boolean;
    measure_advertising_performance: boolean;
    measure_content_performance: boolean;
    save_and_communicate_privacy_choices: boolean;
    store_and_or_access_information_on_a_device: boolean;
    understand_audiences_through_statistics_or_combinations_of_data: boolean;
    use_limited_data_to_select_advertising: boolean;
    use_limited_data_to_select_content: boolean;
    use_precise_geolocation_data: boolean;
    use_profiles_to_select_personalised_advertising: boolean;
    use_profiles_to_select_personalised_content: boolean;
  };
  vendors: string[];
}

@injectable()
export class Ketch implements ConsentProviderInterface {
  // We need to ensure proper amount of consents in GDPR window. At this point we assume that it works like in TOI - We need all the consents to turn on the tracking.
  // It should be changed after we figure out necessary consents to turn on Tracking and apply that rule to both - TOI and Ketch
  private KETCH_PURPOSE_COUNT = 16;

  constructor(
    @injectWithTransform(Logger, LoggerTransformer, "Ketch")
    private readonly logger: Logger,
    @inject(WINDOW) private readonly window: Window,
    @inject(Geo) private readonly geo: Geo,
    @inject(KetchTracker) private readonly ketchTracker: KetchTracker,
  ) {
    this.logger.info("Ketch initialized");
    this.ketchTracker.init();
  }

  userInteractedWithModal() {
    return false;
  }

  checkUserConsent(setup: KetchSetup): boolean {
    return (
      Object.values(setup.purposes).filter((val) => val).length >=
      this.KETCH_PURPOSE_COUNT
    );
  }

  addKetchListener(callback: ConsentResolvedCallback): void {
    waitFor(() => !!this.window.ketch).then(() => {
      this.addTrackers();
      this.window.ketch(
        KetchCommands.on,
        KetchEvents.consent,
        (res: KetchSetup) => {
          if (
            res?.purposes &&
            Object.keys(res.purposes).length >= this.KETCH_PURPOSE_COUNT
          ) {
            this.logger.info("Ketch consent event received", res);
            const userConsent: boolean = this.checkUserConsent(res);
            callback({
              ccpaSignal: !userConsent,
              geoRequiresSignal: this.geo.getCountryCode() === "us",
              gdprConsent: userConsent,
              geoRequiresConsent: this.geo.isGdprCountry(),
            });
          } else {
            this.logger.info("Ketch consent is not ready", res);
          }
        },
      );
    });
  }

  addTrackers() {
    if (document.getElementById("ketch-banner")) {
      this.ketchTracker.sendImpressionEvent();
    } else {
      this.logger.info("Ketch banner not found");
    }
    this.window.ketch(KetchCommands.on, KetchEvents.willShowExperience, () => {
      this.window.ketch(KetchCommands.once, KetchEvents.consent, () => {
        this.ketchTracker.sendImpressionEvent();
      });
    });

    this.window.ketch(
      KetchCommands.on,
      KetchEvents.userConsentUpdated,
      (res: KetchSetup) => {
        const userConsent: boolean = this.checkUserConsent(res);
        this.ketchTracker.sendActionEvent(userConsent ? "accept" : "reject");
      },
    );

    this.logger.info("Trackers added");
  }

  subscribe(callback: ConsentResolvedCallback): void {
    this.logger.info("Subscribed");

    this.addKetchListener(callback);
  }
}
